import { type OnDropArgs, SortableTree } from "@common/controls/TreeView/Dnd/Inner/component";
import { type TreeItem, type TreeItemComponentProps } from "@common/controls/TreeView/Dnd/Inner/types";
import { flattenTree } from "@common/controls/TreeView/Dnd/Inner/utilities";
import { SortableContainerHOC } from "@common/hocs/dnd";
import { type UniqueIdentifier } from "@dnd-kit/core";
import * as React from "react";
import { Group, Row, SortableRow } from "./Row/component";
import { type GridProps } from "./component";
import { isGroupItem } from "./lib";
import { type GridGroupInfo } from "./types";

type RowListProps<T extends {}> = GridProps<T> & {
  idKey: Extract<keyof T, string>;
  gridRowMenu?: (rowData: T) => React.ReactNode;
  isMenuAvailable?: (rowData: T) => boolean;
  isRowHighlighted?: (rowData: T) => boolean;
};

const RowList = <T extends {}>({
  columns,
  data,
  gridRowMenu,
  gridGroupMenu,
  hasGroupMenu,
  hasMenu,
  getRowClassName,
  idKey,
  isMenuAvailable,
  isRowHighlighted,
  onCheckboxClick,
  onRowClick,
  onRowMove,
  renderGroup,
  selectedItems,
  tooltip
}: RowListProps<T>) => (
  <>
    {data.map((x, i) => {
      const Component = data.length > 1 && !!onRowMove ? SortableRow : Row;

      return isGroupItem(x) ? (
        <Group
          key={i} // IMPORTANT: don't use idKey, as it's buggy
          colSpan={columns.length + 1}
          data={x}
          groupMenu={!!gridGroupMenu ? gridGroupMenu(x) : undefined}
          hasMenu={hasGroupMenu}
          isMenuAvailable={isMenuAvailable}
          isRowHighlighted={isRowHighlighted}
          onClick={onRowClick}
          render={renderGroup}
        />
      ) : (
        <Component
          key={i} // IMPORTANT: don't use idKey, as it's buggy
          className={getRowClassName ? getRowClassName(x) : undefined}
          columns={columns}
          data={x}
          dragMode={!!onRowMove && data.length > 1 ? "handle" : undefined}
          hasMenu={hasMenu}
          id={x[idKey] as any}
          idKey={idKey}
          index={i}
          isMenuAvailable={isMenuAvailable}
          isRowHighlighted={isRowHighlighted}
          onCheckboxClick={onCheckboxClick}
          onClick={onRowClick}
          rowMenu={!!gridRowMenu ? gridRowMenu(x) : undefined}
          selectedItems={selectedItems}
          tooltip={tooltip ? tooltip(x) : undefined}
        />
      );
    })}
  </>
);

export default RowList;

export const MovableRowList = SortableContainerHOC<RowListProps<any>>(RowList);

type Id<T extends {}> = Extract<keyof T, string | number>;
type DndTreeRowListBaseProps<T extends {}> = RowListProps<T> & {
  onDrop?: (args: OnDropArgs<TreeItem<T>>) => void;
  parentIdProp: Id<T>;
};

type RowTreeItem<T extends {}> = TreeItem<T | GridGroupInfo>;

const DndTreeRowListBase = <T extends {}>({ activeId, ...props }: DndTreeRowListBaseProps<T>) => {
  const [items, setItems] = React.useState<RowTreeItem<T>[]>([]);

  React.useEffect(() => {
    const map = (parentId: UniqueIdentifier | null, item: T | GridGroupInfo, depth: number) => {
      const isGroup = isGroupItem(item);
      const id = isGroup ? item.id : (item[props.idKey] as UniqueIdentifier);
      const node: RowTreeItem<T> = {
        id,
        data: item,
        depth,
        droppable: true,
        parentId
      };

      const children = props.data.filter(
        (x) => !isGroupItem(x) && x[props.parentIdProp] === (isGroupItem(item) ? item.id : item[props.idKey])
      ) as T[];

      if (children.length) {
        node.children = [];

        for (const child of children) {
          const childNode = map(node.id, child, depth + 1);
          node.children.push(childNode);
        }
      }

      return node;
    };

    setItems(props.data.filter((x) => isGroupItem(x) || !x[props.parentIdProp]).map((x) => map(null, x, 1)));
  }, [props.data, props.parentIdProp, props.idKey]);

  const RowComponent = React.useMemo(
    () => (cp: TreeItemComponentProps<T>) => {
      const levelItems = cp.item.parentId
        ? items.filter((x) => x.id === cp.item.parentId)
        : items.filter((x) => !x.parentId);
      const hasGroup = props.data.some((x) => isGroupItem(x));

      if (isGroupItem(cp.item.data)) {
        return (
          <Group
            setRef={cp.setRef}
            key={cp.item.data.id}
            colSpan={props.onDrop ? props.columns.length + 2 : props.columns.length + 1}
            data={cp.item.data}
            groupMenu={props.gridGroupMenu ? props.gridGroupMenu(cp.item.data) : undefined}
            hasMenu={props.hasGroupMenu}
            isMenuAvailable={props.isMenuAvailable}
            isRowHighlighted={props.isRowHighlighted}
            style={cp.style}
          />
        );
      }

      return (
        <Row
          setRef={cp.setRef}
          key={cp.item.data[props.idKey] as any}
          className={props.getRowClassName ? props.getRowClassName(cp.item.data) : undefined}
          columns={props.columns}
          data={cp.item.data}
          dragMode={props.onDrop ? (hasGroup ? "handle" : props.data.length > 1 ? "row" : undefined) : undefined}
          expandMode={props.expandMode}
          hasMenu={props.hasMenu}
          id={cp.item.data[props.idKey] as any}
          idKey={props.idKey}
          indent={hasGroup ? undefined : cp.indentationWidth * cp.depth}
          expand={
            hasGroup
              ? undefined
              : !!cp.item.children?.length
              ? cp.isExpanded
                ? "collapse"
                : "expand"
              : cp.depth > 1 || levelItems.some((x) => !!x.children?.length)
              ? "hidden"
              : undefined
          }
          isMenuAvailable={props.isMenuAvailable}
          isRowHighlighted={props.isRowHighlighted}
          onClick={props.onRowClick}
          rowMenu={!!props.gridRowMenu ? props.gridRowMenu(cp.item.data) : undefined}
          toggleExpanded={!!cp.item.children?.length ? cp.toggleExpanded : undefined}
          handleProps={cp.handleProps}
          style={cp.style}
        />
      );
    },
    [
      props.columns,
      props.data,
      props.hasMenu,
      props.idKey,
      props.isMenuAvailable,
      props.isRowHighlighted,
      props.onDrop,
      props.onRowClick,
      props.gridGroupMenu,
      items
    ]
  );

  return (
    <SortableTree
      activeId={activeId}
      dropAnimation={null}
      indentationWidth={20}
      isNoAnimation
      isSameDepthDrag={false}
      items={items}
      mode="active"
      onDrop={props.onDrop}
      TreeItemComponent={RowComponent}
    />
  );
};

type DndTreeRowListProps<T extends {}> = Omit<DndTreeRowListBaseProps<T>, "onDrop"> & {
  onDrop: (args: OnDropArgs<T>) => void;
};
export const DndTreeRowList = <T extends {}>(props: DndTreeRowListProps<T>) => {
  const onDrop = React.useCallback(
    (args: OnDropArgs<TreeItem<T>>) => {
      const flat = flattenTree(args.items);

      props.onDrop({
        index: args.index,
        parentId: args.parentId,
        items: flat.map((x) => (x.id === args.source.id ? { ...x.data, [props.parentIdProp]: args.parentId } : x.data)),
        source: args.source.data
      });
    },
    [props.parentIdProp, props.onDrop]
  );

  return <DndTreeRowListBase {...props} onDrop={onDrop} />;
};

type TreeRowListProps<T extends {}> = Omit<DndTreeRowListBaseProps<T>, "onDrop">;
export const TreeRowList = <T extends {}>(props: TreeRowListProps<T>) => <DndTreeRowListBase {...props} />;
